import { useMemo } from "react";
import moment from "moment";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ContentContainer from "@/components/containers/contentContainer";
import RequestQuoteProposalsView from "@/pages/shell/clientManagement/request-quote-proposals/RequestQuoteProposalsView";
import { trackUserAction } from "@/utils/analytics";

type LocationNavState = {
  job?: {
    _id?: string;
    uid?: string;
    createdAt?: string;
    service?: { name?: string } | string;
    description?: string;
  };
};

const getServiceName = (job: LocationNavState["job"]) => {
  if (!job) return "-";
  if (typeof job.service === "object" && job.service?.name) return job.service.name;
  if (typeof job.service === "string") return job.service;
  return "-";
};

export default function RequestQuoteProposalsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestQuoteJobId } = useParams<{ requestQuoteJobId: string }>();
  const location = useLocation();
  const navState = (location.state || {}) as LocationNavState;
  const job = navState.job;

  const jobId = useMemo(
    () => requestQuoteJobId || job?._id || "",
    [requestQuoteJobId, job?._id]
  );

  const handleAccepted = (acceptedJob: any) => {
    trackUserAction("request_quote_proposal_accepted", {
      job_id: acceptedJob?._id || jobId,
      user_type: "client",
    });
    navigate("/my-proposals");
  };

  if (!jobId) {
    return (
      <ContentContainer className="!px-0 m-0 bg-[#f9f9ff] min-h-screen">
        <div className="max-w-[720px] mx-auto px-4 py-10 flex flex-col gap-4">
          <p className="text-sm text-[#424654]">{t("requestQuoteJobNotFound")}</p>
          <button
            type="button"
            onClick={() => navigate("/request-quotes")}
            className="w-fit h-10 px-5 rounded-lg border border-[#d8e3fb] bg-white text-sm font-medium text-[#4285F4]"
          >
            ← {t("back")}
          </button>
        </div>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer className="!px-0 m-0 bg-[#f9f9ff] min-h-screen pb-8">
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 h-14 bg-[#f9f9ff]/80 backdrop-blur-md shadow-sm">
        <button
          type="button"
          className="size-11 rounded-full flex items-center justify-center text-[#111c2d] text-lg"
          onClick={() => navigate("/request-quotes")}
          aria-label={t("back")}
        >
          ←
        </button>
        <h1 className="text-xl font-semibold text-[#111c2d]">
          {t("view_proposals")}
        </h1>
      </div>

      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 pt-6 flex flex-col gap-6 min-w-0">
        {job && (
          <div className="bg-[#e7eeff] rounded-xl p-4 shadow-sm flex flex-col gap-4 min-w-0 overflow-hidden">
            <div className="flex items-center gap-3">
              <div className="size-8 shrink-0 rounded-full bg-[#4285F4]/10 flex items-center justify-center text-[#4285F4] text-sm font-bold">
                ✓
              </div>
              <h2 className="text-xl font-semibold text-[#111c2d]">
                {t("requestSummary")}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
              <div className="min-w-0">
                <p className="text-xs font-medium text-[#424654]">
                  {t("jobId")}
                </p>
                <p
                  className="text-sm font-semibold text-[#111c2d] mt-0.5 truncate"
                  title={job.uid || job._id}
                >
                  {job.uid || job._id || jobId}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-[#424654]">
                  {t("serviceType")}
                </p>
                <p
                  className="text-sm text-[#111c2d] mt-0.5 truncate"
                  title={getServiceName(job)}
                >
                  {getServiceName(job)}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-[#424654]">
                  {t("proposedDate")}
                </p>
                <p className="text-sm text-[#111c2d] mt-0.5">
                  {job.createdAt
                    ? moment(job.createdAt).format("DD MMM YYYY")
                    : "—"}
                </p>
              </div>
            </div>

            {job.description && (
              <div className="min-w-0 pt-1 border-t border-[#d8e3fb]/60">
                <p className="text-xs font-medium text-[#424654]">
                  {t("description")}
                </p>
                <p className="text-sm text-[#111c2d] mt-1 break-words whitespace-pre-wrap [overflow-wrap:anywhere] max-w-full">
                  {job.description}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#111c2d]">
            {t("proposals")}
          </h3>
          <RequestQuoteProposalsView
            requestQuoteJobId={jobId}
            onAccepted={handleAccepted}
          />
        </div>
      </div>
    </ContentContainer>
  );
}
